import React, { useRef, useState, useEffect, useMemo } from "react";
import "../common/AddNewLicensePanel.css";
import Icon from "../../../common/Icon";
import { toast } from "react-toastify";
import PageLoader from "../../../shared/PageLoader";
import cloudUpload from "./../../../../assets/img/icon/cloudUpload.svg";
import { getUserDetails } from "../../../../backend/auth";
import {
  useGetLicenseTypes,
  useGetApplicationTypes,
  useCreateCompanyLicense,
  useGetCompanyLicenses,
} from "../../../../backend/license";
import { useGetUserCompanies } from "../../../../backend/query";
import { useGetStates, useGetCities } from "../../../../backend/masters";
import { useGetUsers } from "../../../../backend/users";
import { post } from "../../../../backend/request";

interface SidePanelProps {
  isOpen: boolean;
  closePanel: () => void;
  onSuccess?: () => void;
}

const SidePanel: React.FC<SidePanelProps> = ({
  isOpen,
  closePanel,
  onSuccess,
}) => {
  const user = getUserDetails();
  const [currentStep, setCurrentStep] = useState(1);

  // Form state
  const [formData, setFormData] = useState<any>({
    company_id: "",
    associate_company_id: "",
    location_id: "",
    license_type_id: "",
    state_id: "",
    city_id: "",
    license_name: "",
    authority: "",
    application_type_id: "",
    is_perpetual: false,
    issue_date: "",
    expiry_date: "",
    default_validity_days: null,
    assigned_to_user_id: "",
    // escalation_user_id: "",
    editor_ids: [],
    watcher_ids: [],
    status: "active",
    description: "",
  });

  // API hooks for dropdowns
  const { userCompanies } = useGetUserCompanies(
    {
      include: [
        "associate_companies",
        "associate_companies.locations",
        "associate_companies.locations.city",
        "associate_companies.locations.city.state"
      ],
      includes: [
        "associate_companies",
        "associate_companies.locations",
        "associate_companies.locations.city",
        "associate_companies.locations.city.state"
      ],
    },
    true
  );

  const [companies, setCompanies] = useState<any[]>([]);
  const [associateCompanies, setAssociateCompanies] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);

  const resetForm = () => {
    setCurrentStep(1);
    setFormData({
      company_id: "",
      associate_company_id: "",
      location_id: "",
      license_type_id: "",
      state_id: "",
      city_id: "",
      license_name: "",
      authority: "",
      application_type_id: "",
      is_perpetual: false,
      issue_date: "",
      expiry_date: "",
      default_validity_days: null,
      assigned_to_user_id: "",
      editor_ids: [],
      watcher_ids: [],
      status: "active",
      description: "",
    });
    setAssociateCompanies([]);
    setLocations([]);
    setUploadedFiles([]);
    setUploadedAssetUrl("");
    setIsUploadingFile(false);
  };

  // Handle close panel with reset
  const handleClosePanel = () => {
    resetForm();
    closePanel();
  };

  // Fetch existing licenses for duplicate check - MUST enable this
  const { licenses: existingLicenses, isFetching: checkingDuplicate } =
    useGetCompanyLicenses(
      {
        filters: {
          associate_company_id_eq: formData.associate_company_id,
          location_id_eq: formData.location_id,
          license_type_id_eq: formData.license_type_id,
          status_in: ["active", "up_for_renewal"],
        },
      },
      Boolean(
        formData.associate_company_id &&
          formData.location_id &&
          formData.license_type_id
      )
    );

  // Process companies
  useEffect(() => {
    if (userCompanies && userCompanies.length > 0) {
      setCompanies(userCompanies);
    }
  }, [userCompanies]);

  // Cascade: Company → Associate Companies
  useEffect(() => {
    if (formData.company_id && companies.length > 0) {
      const selectedCompany = companies.find(
        (c: any) => c.id === formData.company_id
      );
      if (selectedCompany && selectedCompany.associate_companies) {
        setAssociateCompanies(selectedCompany.associate_companies);
      } else {
        setAssociateCompanies([]);
      }
      setFormData((prev: any) => ({
        ...prev,
        associate_company_id: "",
        location_id: "",
      }));
      setLocations([]);
    } else {
      setAssociateCompanies([]);
    }
  }, [formData.company_id, companies]);

  // Cascade: Associate Company → Locations
  useEffect(() => {
    if (formData.associate_company_id && associateCompanies.length > 0) {
      const selectedAssocCompany = associateCompanies.find(
        (ac: any) => ac.id === formData.associate_company_id
      );
      if (selectedAssocCompany && selectedAssocCompany.locations) {
        setLocations(selectedAssocCompany.locations);
      } else {
        setLocations([]);
      }
      setFormData((prev: any) => ({
        ...prev,
        location_id: "",
      }));
    } else {
      setLocations([]);
    }
  }, [formData.associate_company_id, associateCompanies]);

  useEffect(() => {
    if (formData.location_id && locations.length > 0) {
      const selectedLocation = locations.find(
        (loc: any) => loc.id === formData.location_id
      );

      if (selectedLocation) {
        const stateId =
          selectedLocation.state_id ||
          (selectedLocation.city && selectedLocation.city.state_id) ||
          (selectedLocation.city && selectedLocation.city.state && selectedLocation.city.state.id) ||
          "";

        const cityId =
          selectedLocation.city_id ||
          (selectedLocation.city && selectedLocation.city.id) ||
          "";
        setFormData((prev: any) => ({
          ...prev,
          state_id: stateId,
          city_id: cityId,
        }));
      } else {
        setFormData((prev: any) => ({
          ...prev,
          state_id: "",
          city_id: "",
        }));
      }
    } else {
      setFormData((prev: any) => ({
        ...prev,
        state_id: "",
        city_id: "",
      }));
    }
  }, [formData.location_id, locations]);

  const { licenseTypes: allLicenseTypes } = useGetLicenseTypes(
    {
      scopes: {
        // Use state-aware scope when state is available
        available_for_location: formData.associate_company_id && formData.state_id
        ? [formData.associate_company_id, formData.state_id]
          : null,
      },
    },
    Boolean(formData.associate_company_id  && formData.state_id)
  );
  const licenseTypes = useMemo(() => {
    if (!allLicenseTypes || !formData.city_id) {
      return allLicenseTypes || [];
    }
    return allLicenseTypes.filter((licenseType: any) => {

      if (!licenseType.city_id) {
        return true;
      }

      return licenseType.city_id === formData.city_id;
    });
  }, [allLicenseTypes, formData.city_id]);
  
  const { states } = useGetStates({}, true);
  const { cities } = useGetCities(
    { filters: { state_id_eq: formData.state_id } },
    Boolean(formData.state_id)
  );
  const { applicationTypes } = useGetApplicationTypes(
    { filters: { license_type_id_eq: formData.license_type_id } },
    Boolean(formData.license_type_id )
  );
  const { records: licenseOwners } = useGetUsers(
    {
      filters: {
        companies_id_in: [formData.associate_company_id],
        company_location_mappings_location_id_in: [formData.location_id],
        roles_name_in: ['License Owner']
      }
    },
    Boolean(formData.associate_company_id && formData.location_id)
  );

  const { records: licenseWatchers } = useGetUsers(
    {
      filters: {
        companies_id_in: [formData.associate_company_id],
        company_location_mappings_location_id_in: [formData.location_id],
        roles_name_in: ['License Watcher']
      }
    },
    Boolean(formData.associate_company_id && formData.location_id)
  );

  // File upload
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedAssetUrl, setUploadedAssetUrl] = useState<string>("");
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  function getLastHyphenValue(str: string | null | undefined): string {
    if (!str) return "";
    const parts = str.split("-");
    return parts[parts.length - 1].trim();
  }

  // Create mutation
  const { createLicense, creating } = useCreateCompanyLicense(
    (response: any) => {
      toast.success("New license added successfully", {
        icon: <Icon name="snackbarSuccess" />,
      });
      resetForm();
      closePanel();
      onSuccess && onSuccess();
    },
    (error: any) => {
      const errorData = error && error.response && error.response.data;
      const errorMessages = errorData && errorData.error_messages;

      if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
        errorMessages.forEach((errMsg: string) => {
          toast.error(errMsg, {
            icon: <Icon name="snackbarError" />
          });
        });
      } else if (errorData && errorData.message) {
        toast.error(errorData.message);
      } else {
        toast.error("Failed to add license");
      }
    }
  );
  useEffect(() => {
    if (formData.license_type_id && applicationTypes && applicationTypes.length > 0) {
      const newAppType = applicationTypes.find(
        (at: any) => at.action_type === 'new' || at.action_type === 'New'
      );

      if (newAppType && !formData.application_type_id) {
        console.log('Auto-selecting "New" application type:', newAppType.id);
        setFormData((prev: any) => ({
          ...prev,
          application_type_id: newAppType.id,
        }));
      }
    }
  }, [formData.license_type_id, applicationTypes]);

  // Auto-fill authority when license type is selected
  useEffect(() => {
    if (formData.license_type_id && licenseTypes) {
      const selectedType = licenseTypes.find(
        (lt: any) => lt.id === formData.license_type_id
      );
      if (selectedType) {
        setFormData((prev: any) => ({
          ...prev,
          authority: (selectedType && selectedType.authority) || "",
          is_perpetual: selectedType.perpetual_allowed || false,
          default_validity_days: selectedType.default_validity_days || null,
        }));
      }
    }
  }, [formData.license_type_id, licenseTypes]);

   useEffect(() => {
    if (formData.issue_date && formData.default_validity_days && !formData.is_perpetual) {
      const issueDate = new Date(formData.issue_date);
      const validityDays = formData.default_validity_days;
      const expiryDate = new Date(issueDate);
      expiryDate.setDate(expiryDate.getDate() + validityDays);

      const formattedExpiry = expiryDate.toISOString().split('T')[0];

      setFormData((prev: any) => ({
        ...prev,
        expiry_date: formattedExpiry,
      }));
    } else if (formData.is_perpetual) {
      // Clear expiry date for perpetual licenses
      setFormData((prev: any) => ({
        ...prev,
        expiry_date: "",
      }));
    }
  }, [formData.issue_date, formData.default_validity_days, formData.is_perpetual]);


  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateStep1 = () => {
    if (!formData.company_id) {
      toast.error("Please select a company");
      return false;
    }
    if (!formData.associate_company_id) {
      toast.error("Please select an associate company");
      return false;
    }
    if (!formData.location_id) {
      toast.error("Please select a company location");
      return false;
    }
    if (!formData.state_id) {
      toast.error("Please select a state");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.license_name) {
      toast.error("Please enter license name");
      return false;
    }
    if (!formData.authority) {
      toast.error("Issuing Authority is required");
      return false;
    }
    if (!formData.license_type_id) {
      toast.error("Please select a license type");
      return false;
    }

    // Check for duplicate license - THIS IS CRITICAL
    if (existingLicenses && existingLicenses.length > 0) {
      const selectedType = licenseTypes.find(
        (lt: any) => lt.id === formData.license_type_id
      );
      const selectedLocation = locations.find(
        (loc: any) => loc.id === formData.location_id
      );
      const selectedAssocCompany = associateCompanies.find(
        (ac: any) => ac.id === formData.associate_company_id
      );

      toast.error(
        `Cannot create license: An active "${
          selectedType.name || "this type"
        }" license already exists for company "${
          selectedAssocCompany.name || ""
        }" at location "${selectedLocation.name || ""}". 
  Please surrender or expire the existing license first.`,
        { autoClose: 8000 }
      );
      return false;
    }

    return true;
  };

  const validateStep3 = () => {
    if (!formData.issue_date) {
      toast.error("Please select issue date");
      return false;
    }
   if (!formData.is_perpetual && !formData.expiry_date) {
    toast.error("Expiry date could not be calculated. Please ensure issue date is provided and license type has validity days configured.");
    return false;
    }
    // Check if expiry date is in the past
    if (!formData.is_perpetual && formData.expiry_date) {
      const expiryDate = new Date(formData.expiry_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to compare dates only

      if (expiryDate < today) {
        toast.error("Cannot create license: The calculated expiry date is in the past. Please check the issue date.");
        return false;
      }
    }
    if (!formData.assigned_to_user_id) {
      toast.error("Please assign to a user");
      return false;
    }
    if (!formData.watcher_ids.length) {
      toast.error("Watcher field is mandatory");
      return false;
    }

    // File upload is required
    if (uploadedFiles.length === 0) {
      toast.error("Please upload the issued license document", {
        autoClose: 5000,
      });
      return false;
    }

    return true;
  };

  const handleDownload = (file: File) => {
    try {
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {}
      }, 1000);
    } catch (err) {
      toast.error("Unable to download file");
    }
  };

  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;

    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Upload file to server and get URL
  // const uploadFileToServer = async (file: File) => {
  //   setIsUploadingFile(true);

  //   try {
  //     const uploadFormData = new FormData();
  //     uploadFormData.append('file', file);
  //     uploadFormData.append('resource_type', 'License::CompanyLicense');
  //     uploadFormData.append('file_type', 'issued_license');

  //     const response = await post('/api/v1/upload', uploadFormData, {
  //       'Content-Type': 'multipart/form-data'
  //     });

  //     if (response && response.data && response.data.url) {
  //       setUploadedAssetUrl(response.data.url);
  //       toast.success("Document uploaded successfully", {
  //         icon: <Icon name="snackbarSuccess" />,
  //       });
  //     } else {
  //       throw new Error("Upload failed - no URL returned");
  //     }
  //   } catch (error: any) {
  //     toast.error(
  //       (error && error.response && error.response.data && error.response.data.error) ||
  //       "Failed to upload document. Please try again."
  //     );
  //     setUploadedFiles([]);
  //     setUploadedAssetUrl("");
  //   } finally {
  //     setIsUploadingFile(false);
  //   }
  // };

  // const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const files = e.target.files ? Array.from(e.target.files) : [];
  //   const validTypes = [
  //     'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
  //     'image/bmp', 'image/webp', 'image/svg+xml', 'application/pdf'
  //   ];

  //   const validFiles = files.filter((file) => {
  //     if (file.size > 10 * 1024 * 1024) {
  //       toast.error(`File ${file.name} exceeds 25MB limit`);
  //       return false;
  //     }
  //     return validTypes.includes(file.type);
  //   });

  //   if (validFiles.length !== files.length) {
  //     toast.error('Only image files (JPG, PNG, GIF, BMP, WEBP, SVG) and PDF are allowed');
  //   }

  //   if (validFiles.length > 0) {
  //     const fileToUpload = validFiles[0];
  //     setUploadedFiles([fileToUpload]);

  //     // Upload immediately
  //     await uploadFileToServer(fileToUpload);
  //   }

  //   if (fileInputRef.current) {
  //     fileInputRef.current.value = '';
  //   }
  // };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/bmp",
      "image/webp",
      "image/svg+xml",
      "application/pdf",
    ];
    const currentFileCount = uploadedFiles.length;
    const newFileCount = files.length;
    const totalFileCount = currentFileCount + newFileCount;

    if (totalFileCount > 5) {
      toast.error(`You can upload a maximum of 5 files. Currently ${currentFileCount} file${currentFileCount !== 1 ? 's' : ''} uploaded.`);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const validFiles = files.filter((file) => {
      if (file.size > 25 * 1024 * 1024) {
        toast.error(`File ${file.name} exceeds 25MB limit`);
        return false;
      }
      return validTypes.includes(file.type);
    });

    if (validFiles.length !== files.length) {
      toast.error(
        "Only image files (JPG, PNG, GIF, BMP, WEBP, SVG) and PDF are allowed"
      );
    }

    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
      // File will be uploaded when user clicks "Done" - no separate upload needed
      setUploadedAssetUrl("ready"); // Mark as ready to submit
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

    // If all files are removed, clear the asset URL
    if (uploadedFiles.length === 1) {
      setUploadedAssetUrl("");
    }
  };

  const handleChooseFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onSubmit = async () => {
    if (!validateStep3()) return;

    const { state_id, city_id, ...validData } = formData;

    // Create FormData for file upload
    const formDataPayload = new FormData();

    uploadedFiles.forEach((file, index) => {
      if (index === 0) {
        formDataPayload.append("company_license[issued_document]", file);
      } else {
        formDataPayload.append(
          `company_license[additional_documents][]`,
          file
        );
      }
    });

    // Add all company_license fields
    Object.keys(validData).forEach((key) => {
      const value = validData[key];

      if (Array.isArray(value)) {
        // Handle arrays (editor_ids, watcher_ids)
        value.forEach((item: any) => {
          if (item) {
            formDataPayload.append(`company_license[${key}][]`, item);
          }
        });
      } else if (value !== null && value !== undefined && value !== "") {
        formDataPayload.append(`company_license[${key}]`, value);
      }
    });

    // Add user tracking
    formDataPayload.append("company_license[created_by_id]", user.user_id);
    formDataPayload.append("company_license[updated_by_id]", user.user_id);

    createLicense(formDataPayload as any);
  };

  // Check for duplicates when license type or location changes
  const hasDuplicate = existingLicenses && existingLicenses.length > 0;

  return (
    <>
      {isOpen && <div className="sidepanel-overlay" onClick={handleClosePanel}></div>}
      <div className="sidepanel-wrapper">
        <div className={`sidepanel ${isOpen ? "open" : ""}`}>
          <div className="sidepanel-header d-flex justify-content-between align-items-center pb-4">
            <h5 className="mb-0 fw-semibold">Add New License</h5>
            <button
              className="btn-close"
              aria-label="Close"
              onClick={handleClosePanel}
            ></button>
          </div>

          <div className="sidepanel-body h-100">
            <ul className="progress-section p-0 m-0 pl-2">
              <li
                className={currentStep === 1 || currentStep > 1 ? "active" : ""}
              >
                <div className="d-flex align-items-center">
                  <div className="progress-circle d-flex justify-content-center align-items-center">
                    <div
                      className={`progress-checked ${
                        currentStep > 1 ? "completed" : ""
                      }`}
                    ></div>
                  </div>
                  <div className="progress-line"></div>
                </div>
                <p>Step 1</p>
                <h6>Basic Details</h6>
              </li>
              <li
                className={currentStep === 2 || currentStep > 2 ? "active" : ""}
              >
                <div className="d-flex align-items-center">
                  <div className="progress-circle d-flex justify-content-center align-items-center">
                    <div
                      className={`progress-checked ${
                        currentStep > 2 ? "completed" : ""
                      }`}
                    ></div>
                  </div>
                  <div className="progress-line"></div>
                </div>
                <p>Step 2</p>
                <h6>License Details</h6>
              </li>
              <li className={currentStep === 3 ? "active" : ""}>
                <div className="progress-circle d-flex justify-content-center align-items-center">
                  <div className="progress-checked"></div>
                </div>
                <p>Step 3</p>
                <h6>Assignment</h6>
              </li>
            </ul>

            <hr className="my-4" />

            {/* Step 1 - Basic Details */}
            {currentStep === 1 && (
              <form>
                <div className="mb-3">
                  <label className="form-label">
                    Company<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.company_id}
                    onChange={(e) =>
                      handleInputChange("company_id", e.target.value)
                    }
                  >
                    <option value="">Select company</option>
                    {(companies || []).map((company: any) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Associate Company<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.associate_company_id}
                    onChange={(e) =>
                      handleInputChange("associate_company_id", e.target.value)
                    }
                    disabled={!formData.company_id}
                  >
                    <option value="">Select associate company</option>
                    {(associateCompanies || []).map((company: any) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Company Location<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.location_id}
                    onChange={(e) =>
                      handleInputChange("location_id", e.target.value)
                    }
                    disabled={!formData.associate_company_id}
                  >
                    <option value="">Select company location</option>
                    {(locations || []).map((location: any) => (
                      <option key={location.id} value={location.id}>
                        {location.name} - {getLastHyphenValue(location.company_location_address)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="form-label">
                    State
                  </label>
                  <select
                    className="form-select"
                    value={formData.state_id}
                    onChange={(e) =>
                      handleInputChange("state_id", e.target.value)
                    }
                    disabled
                  >
                    <option value="">Select state</option>
                    {(states || []).map((state: any) => (
                      <option key={state.id} value={state.id}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="form-label">
                    City
                  </label>
                  <select
                    className="form-select"
                    value={formData.city_id}
                    onChange={(e) =>
                      handleInputChange("city_id", e.target.value)
                    }
                    disabled
                  >
                    <option value="">City</option>
                    {(cities || []).map((city: any) => (
                      <option key={city.id} value={city.id}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>
              </form>
            )}

            {/* Step 2 - License Details */}
            {currentStep === 2 && (
              <form>
                <div className="mb-3">
                  <label className="form-label">
                    License Name<span>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter a unique license name"
                    value={formData.license_name}
                    onChange={(e) =>
                      handleInputChange("license_name", e.target.value)
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    License Type<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.license_type_id}
                    onChange={(e) =>
                      handleInputChange("license_type_id", e.target.value)
                    }
                    disabled={!formData.associate_company_id}
                  >
                    <option value="">Select license type</option>
                    {(licenseTypes || []).map((type: any) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                <input
                  type="hidden"
                  name="application_type_id"
                  value={formData.application_type_id || ''}
                />

                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    id="description"
                    placeholder="Add description"
                    value={formData.description || ""}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                  ></textarea>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Issuing Authority<span>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Authority"
                    value={formData.authority}
                    onChange={(e) =>
                      handleInputChange("authority", e.target.value)
                    }
                    // readOnly
                  />
                </div>

                {/* Show duplicate warning - CRITICAL CHECK */}
                {/* {checkingDuplicate && (
                  <div className="alert alert-info" role="alert">
                    Checking for existing licenses...
                  </div>
                )} */}
              </form>
            )}

            {/* Step 3 - Assignment */}
            {currentStep === 3 && (
              <form>
                <div className="mb-3 d-flex align-items-center gap-5">
                  <div>
                    <label className="form-label mb-0 me-auto">
                      Is Perpetual
                    </label>
                    <p className="mb-0 me-2 text-gray-12">
                      {formData.is_perpetual
                        ? "This license type is perpetual (does not expire)"
                        : "This license type expires and requires renewal"}
                    </p>
                  </div>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="isPerpetual"
                      checked={formData.is_perpetual}
                      disabled={true}
                      title="This is set automatically based on the license type"
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Issued On<span>*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.issue_date}
                    onChange={(e) =>
                      handleInputChange("issue_date", e.target.value)
                    }
                  />
                </div>

                {!formData.is_perpetual && (
                  <div className="mb-3">
                    <label className="form-label">
                      Expiry Date
                    </label>
                    <input
                      type="date"
                      className="form-control bg-light"
                      value={formData.expiry_date}
                      readOnly
                      disabled
                    />
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">
                    Assigned to<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.assigned_to_user_id}
                    onChange={(e) =>
                      handleInputChange("assigned_to_user_id", e.target.value)
                    }
                  >
                    <option value="">Select an owner who will manage this license end to end</option>
                    {(licenseOwners || []).map((user: any) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* <div className="mb-3">
                  <label className="form-label">Escalation Point</label>
                  <select
                    className="form-select"
                    value={formData.escalation_user_id}
                    onChange={(e) =>
                      handleInputChange("escalation_user_id", e.target.value)
                    }
                  >
                    <option value="">Select User</option>
                    {(users || []).map((user: any) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div> */}

                <div className="mb-3">
                  <label className="form-label">Watchers<span>*</span></label>
                  <div
                    className="watchers-box p-3"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    {(licenseWatchers || []).map((watcher: any) => (
                      <div
                        key={watcher.id}
                        className="mb-3 d-flex gap-2 align-items-start"
                      >
                        <input
                          className="form-check"
                          type="checkbox"
                          id={`watcher-${watcher.id}`}
                          checked={formData.watcher_ids.includes(watcher.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData((prev: any) => ({
                              ...prev,
                              watcher_ids: checked
                                ? [...prev.watcher_ids, watcher.id]
                                : prev.watcher_ids.filter(
                                    (id: string) => id !== watcher.id
                                  ),
                            }));
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`watcher-${watcher.id}`}
                        >
                          <div className="fs-6">{watcher.name}</div>
                          <small className="text-sm">{watcher.email}</small>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <div>
                    <label className="form-label">
                      Upload License Copy<span>*</span>
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleFileSelect}
                      style={{ display: "none" }}
                      disabled={isUploadingFile}
                      multiple
                    />

                    {uploadedFiles.length === 0 && (
                      <div
                        className="p-4 text-center upload-box"
                        onClick={handleChooseFile}
                        style={{
                          cursor: isUploadingFile ? "not-allowed" : "pointer",
                          opacity: isUploadingFile ? 0.6 : 1,
                        }}
                      >
                        <div className="mb-2">
                          <img
                            src={cloudUpload}
                            alt="cloudUpload"
                            className="me-2"
                          />
                        </div>
                        <p className="mb-2 text-md">
                          {isUploadingFile
                            ? "Uploading..."
                            : "Click to browse and upload"}
                        </p>
                        <button
                          type="button"
                          className="mb-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChooseFile();
                          }}
                          disabled={isUploadingFile}
                        >
                          {isUploadingFile ? "Uploading..." : "Choose File"}
                        </button>
                        <div>
                          <small className="text-gray-12 d-block">
                            Supported formats (Up to 25 MB per file): JPG, PNG, JPEG, WEBP, SVG, PDF
                          </small>
                        </div>
                      </div>
                    )}

                    {uploadedFiles.length > 0 &&
                      uploadedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="upload-successful position-relative mb-3"
                        >
                          <div
                            className="close-button position-absolute"
                            style={{
                              cursor: "pointer",
                              // top: "10px",
                              // right: "10px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFile(index);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M10.0002 5.99992L6.00016 9.99992M6.00016 5.99992L10.0002 9.99992M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z"
                                stroke="#757575"
                                strokeWidth="1.33333"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <div className="p-4 d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-2" style={{width: "90%"}}>
                              <p className="text-sm m-0 ellipse">{file.name}</p>
                              {/* {uploadedAssetUrl && (
                                <span className="badge bg-success">
                                  ✓ Uploaded
                                </span>
                              )} */}
                              {isUploadingFile && (
                                <span className="badge bg-warning">
                                  Uploading...
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                cursor: "pointer",
                                height: "25px",
                                width: "25px",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(file);
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M20.9902 15V19C20.9902 19.5304 20.7795 20.0391 20.4044 20.4142C20.0294 20.7893 19.5207 21 18.9902 21H4.99023C4.4598 21 3.95109 20.7893 3.57602 20.4142C3.20095 20.0391 2.99023 19.5304 2.99023 19V15M6.99023 10L11.9902 15M11.9902 15L16.9902 10M11.9902 15V3"
                                  stroke="#2965AD"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className={`button-container ${isOpen ? "open" : ""}`}>
          {currentStep === 1 && (
            <div className="w-100">
              <button
                type="button"
                className="button-primary w-100"
                onClick={handleNext}
              >
                Next
              </button>
            </div>
          )}
          {currentStep === 2 && (
            <>
              {hasDuplicate && (
                <div className="alert alert-danger mb-3" role="alert" style={{ fontSize: '14px' }}>
                  <strong>Duplicate License:</strong> A license already exists with this license type for the selected company, associate company and location.
                </div>
              )}
              <div className="d-flex gap-3 w-100">
                <button
                  type="button"
                  className="button-secondary w-100"
                  onClick={handleBack}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="button-primary w-100"
                  onClick={handleNext}
                  disabled={hasDuplicate || checkingDuplicate}
                >
                  Next
                </button>
              </div>
            </>
          )}
          {currentStep === 3 && (
            <div className="d-flex gap-3 w-100">
              <button
                type="button"
                className="button-secondary w-100"
                onClick={handleBack}
              >
                Back
              </button>
              <button
                type="button"
                className="button-primary w-100"
                onClick={onSubmit}
                // disabled={creating || uploadedFiles.length === 0}
              >
                {creating ? "Saving..." : "Done"}
              </button>
            </div>
          )}
        </div>
      </div>
      {creating && <PageLoader />}
    </>
  );
};

export default SidePanel;
